import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  TakeProfitContext,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTakeProfitErrors, selectTakeProfitTile } from '@/store/orders/takeProfitOrder/selectors.ts';
import { useDebounce } from '@/utils/hooks/debounce.ts';

export const TakeProfitLimitPrice = () => {
  const { tileId, setIsModifyingField } = useContext(TakeProfitContext);
  const track = useTrackEvent();

  const { value } = useAppSelector((state) => {
    const tile = selectTakeProfitTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[TakeProfitSpot] Tile with id ${tileId} not found in state.`);
      return {};
    }
    if (tile.type !== 'TakeProfitOrder') {
      console.error(`[TakeProfitSpot] Tile with id ${tileId} is not a TakeProfitOrder.`);
      return {};
    }

    return { value: tile.properties.limitPrice };
  }, deepEqual);

  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));
  const limitPriceError = errors?.limitPrice;

  const validateTakeProfit = useValidateTakeProfit();

  const handleLimitPriceChange = useDebounce((newValue: number | undefined) => {
    track('Order action', 'Limit price changed');
    validateTakeProfit({
      limitPrice: newValue,
    });
  }, 600);

  return (
    <OrderRow labelId="order.fields.limitPrice.label">
      <OmsFieldErrorWrapper errors={limitPriceError}>
        <SgNumberInput
          valuePosition="start"
          selectAllOnFocus
          size="sm"
          value={value}
          data-e2e="limit-price"
          onChange={handleLimitPriceChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          maxDecimals={9}
          state={isDefined(limitPriceError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
